import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import * as amplitude from "@amplitude/unified";
import Navbar from "./Navbar";
import "./CareerPage.css";

const jobOpenings = [
  {
    id: "bde-intl",
    title: "Senior Business Development Executive (International Sales)",
    badge: "2–3+ Yrs",
    theme: "blue",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <rect x="2" y="7" width="20" height="14" rx="2" />
        <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
        <line x1="12" y1="12" x2="12" y2="16" />
        <line x1="10" y1="14" x2="14" y2="14" />
      </svg>
    ),
    details: [
      { icon: "💼", label: "Experience", value: "2–3+ Years" },
      { icon: "👤", label: "Role", value: "International Sales / IT Services" },
      { icon: "🎯", label: "Focus", value: "Lead Generation, Client Handling, Deal Closure" },
      { icon: "🔧", label: "Skills", value: "Sales, Negotiation, Communication, CRM, LinkedIn, International Market" },
    ],
  },
  {
    id: "mern-dev",
    title: "Senior MERN Stack Developer",
    badge: "2–3+ Yrs",
    theme: "purple",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
      </svg>
    ),
    details: [
      { icon: "💼", label: "Experience", value: "2–3+ Years" },
      { icon: "👤", label: "Role", value: "Full Stack Development" },
      { icon: "🎯", label: "Focus", value: "Build scalable web applications using MERN stack" },
      { icon: "🔧", label: "Skills", value: "MongoDB, Express.js, React.js, Node.js, JavaScript, APIs" },
    ],
  },
  {
    id: "marketing-exec",
    title: "Senior Digital Marketing Executive (Performance Marketing & Lead Generation)",
    badge: "1.5+ Yrs",
    theme: "green",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </svg>
    ),
    details: [
      { icon: "💼", label: "Experience", value: "1.5+ Years" },
      { icon: "👤", label: "Role", value: "Performance Marketing & Lead Generation" },
      { icon: "🎯", label: "Focus", value: "Paid Ads, Funnels, Lead Gen, Analytics & Optimization" },
      { icon: "🔧", label: "Skills", value: "Google Ads, Meta Ads, GA4, GTM, Landing Pages, SEO Basics" },
    ],
  },
];

const hiringInfoItems = [
  {
    icon: "📍",
    label: "Location",
    detail: "651-A, Mahalaxmi Nagar, Indore, Madhya Pradesh",
  },
  {
    icon: "🏢",
    label: "Work Mode",
    detail: "Work From Office (In-Person)",
  },
  {
    icon: "📅",
    label: "Working Days",
    detail: "Monday – Friday (5 Days a Week)",
  },
  {
    icon: "💼",
    label: "Employment Type",
    detail: "Full-Time | Permanent Role",
  },
];

export default function CareerPage() {
  const lifeImages = [
    "/images/life1.jpg",
    "/images/life3.jpg",
    "/images/life2.jpg",
  ];
  const [currentSlide, setCurrentSlide] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const slideTimer = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    slideTimer.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % lifeImages.length);
    }, 3500);
    return () => clearInterval(slideTimer.current);
  }, [lifeImages.length]);

  // Handle ESC key for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && lightbox) {
        setLightbox(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightbox]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    clearInterval(slideTimer.current);
    slideTimer.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % lifeImages.length);
    }, 3500);
  };

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    jobTitle: "",
    experience: "",
    skills: "",
    education: "",
    additional: "",
  });

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleApplyClick = (jobTitle) => {
    setFormData((prev) => ({ ...prev, jobTitle }));
    amplitude.track("Job Application Started", { job_title: jobTitle });
    const section = document.getElementById("resume-form-section");
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleRemoveFile = (e) => {
    e.stopPropagation();
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    const form = new FormData();
    form.append("name", `${formData.firstName} ${formData.lastName}`.trim());
    form.append("email", formData.email);
    form.append("phone", formData.phone);
    form.append("subject", `Resume Submission: ${formData.jobTitle || "General Application"}`);
    form.append(
      "message",
      `Job Title: ${formData.jobTitle}\nExperience: ${formData.experience}\nSkills: ${formData.skills}\nEducation: ${formData.education}\nAdditional: ${formData.additional}`
    );
    if (file) {
      form.append("attachment", file);
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/send-email`, {
        method: "POST",
        body: form,
      });
      const result = await response.json();
      setLoading(false);

      if (result.success) {
        amplitude.track("Job Application Submitted", {
          job_title: formData.jobTitle,
          has_resume: !!file,
        });
        setSuccessMessage("Thank you! Your resume and application have been submitted successfully. Our talent team will review your profile shortly.");
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          jobTitle: "",
          experience: "",
          skills: "",
          education: "",
          additional: "",
        });
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      } else {
        setErrorMessage("Submission failed. Please check your details and try again.");
      }
    } catch (error) {
      console.error("Submission Error:", error);
      setLoading(false);
      setErrorMessage("An unexpected error occurred while sending. Please try again or email us directly at contact@itsoftlab.com.");
    }
  };

  return (
    <>
      <Navbar />
      <main className="careers-main">
        {/* Background Ambient Video */}
        <div className="video-container" aria-hidden="true">
          <video autoPlay loop muted playsInline>
            <source
              src="https://cdn.pixabay.com/video/2020/08/12/46965-449623750_large.mp4"
              type="video/mp4"
            />
          </video>
          <div className="video-overlay"></div>
        </div>

        {/* Hero Section */}
        <section className="careers-hero-section">
          <div className="careers-hero-inner">
            <div className="careers-hero-pill">
              <span className="careers-hero-pill-dot"></span>
              Join the ITSoftLab Team
            </div>
            <h1 className="careers-hero-title">Build Your Future in Technology</h1>
            <p className="careers-hero-subtitle">
              Join a high-impact team of engineers, innovators, and problem-solvers.
              Create world-class digital products and advance your career in a dynamic,
              collaborative environment.
            </p>
            <div className="careers-hero-actions">
              <a href="#openings-section" className="careers-hero-btn-primary">
                View Open Positions &nbsp;↓
              </a>
              <a href="#resume-form-section" className="careers-hero-btn-secondary">
                Submit Your Resume &nbsp;→
              </a>
            </div>
          </div>
        </section>

        {/* Life at ITSoftLab Section */}
        <section className="life-section" id="life-section">
          <div className="life-container">
            <div className="life-card-wrapper">
              <div className="life-text-column">
                <div className="life-badge">
                  <span className="life-badge-dot"></span>
                  CULTURE &amp; COMMUNITY
                </div>
                <h2 className="life-title">Life at ITSoftLab</h2>
                <p className="life-lead">
                  At ITSoftLab, we believe exceptional technology is crafted by empowered,
                  well-connected people. A great workplace is built on strong relationships,
                  mutual respect, and shared experiences.
                </p>
                <p className="life-paragraph">
                  From team excursions like our annual visit to Nakhrali Dhani Water Park
                  to technical hackathons, knowledge-sharing sprints, and wellness days,
                  we nurture an environment where talent thrives. Our commitment to work-life
                  harmony ensures you have the freedom to innovate, unwind, and grow together.
                </p>
                <div className="life-stats-row">
                  <div className="life-stat-box">
                    <span className="life-stat-num">5 Days</span>
                    <span className="life-stat-label">Work Week</span>
                  </div>
                  <div className="life-stat-box">
                    <span className="life-stat-num">100%</span>
                    <span className="life-stat-label">Collaborative</span>
                  </div>
                  <div className="life-stat-box">
                    <span className="life-stat-num">Fast-Track</span>
                    <span className="life-stat-label">Career Growth</span>
                  </div>
                </div>
              </div>

              <div className="life-media-column">
                <div className="life-slider-card">
                  <div className="life-slider-frame">
                    {lifeImages.map((src, index) => (
                      <img
                        key={index}
                        src={src}
                        alt={`Life at ITSoftLab team moment ${index + 1}`}
                        className={`life-slide ${index === currentSlide ? "active" : ""}`}
                        onClick={() => setLightbox(src)}
                        style={{ cursor: "zoom-in" }}
                        loading="lazy"
                      />
                    ))}
                  </div>

                  <div className="life-slider-footer">
                    <span className="life-slider-counter">
                      Photo {currentSlide + 1} of {lifeImages.length}
                    </span>
                    <div className="life-slider-dots">
                      {lifeImages.map((_, index) => (
                        <button
                          key={index}
                          className={`life-dot ${index === currentSlide ? "active" : ""}`}
                          onClick={() => goToSlide(index)}
                          aria-label={`Go to slide ${index + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Lightbox Modal */}
            {lightbox && (
              <div
                className="lightbox-overlay"
                onClick={() => setLightbox(null)}
                role="dialog"
                aria-modal="true"
                aria-label="Image preview"
              >
                <button
                  className="lightbox-close"
                  onClick={() => setLightbox(null)}
                  aria-label="Close full view"
                >
                  ✕
                </button>
                <img
                  src={lightbox}
                  alt="Full view of life at ITSoftLab"
                  className="lightbox-img"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
          </div>
        </section>

        {/* Job Openings Section */}
        <section className="hiring-section" id="openings-section">
          <div className="hiring-container">
            <div className="hiring-badge">
              <span className="hiring-badge-line"></span>
              CURRENT OPPORTUNITIES
              <span className="hiring-badge-line"></span>
            </div>
            <h2 className="hiring-main-title">Build the Future with ITSoftLab</h2>
            <p className="hiring-subtitle">
              We are actively seeking passionate, talented, and driven professionals
              ready to shape the next generation of digital enterprise solutions.
            </p>

            <div className="hiring-cards-grid">
              {jobOpenings.map((job) => (
                <div key={job.id} className={`hiring-card hiring-card--${job.theme}`}>
                  <div className="hiring-card-topbar">
                    <div className={`hiring-card-icon hiring-card-icon--${job.theme}`}>
                      {job.icon}
                    </div>
                    <span className={`hiring-exp-badge hiring-exp-badge--${job.theme}`}>
                      {job.badge}
                    </span>
                  </div>

                  <h3 className="hiring-card-title">{job.title}</h3>

                  <ul className="hiring-card-details">
                    {job.details.map((detail, idx) => (
                      <li key={idx}>
                        <span className="hcd-icon" aria-hidden="true">{detail.icon}</span>
                        <div>
                          <strong>{detail.label}:</strong> {detail.value}
                        </div>
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    className={`hiring-view-btn hiring-view-btn--${job.theme}`}
                    onClick={() => handleApplyClick(job.title)}
                  >
                    Apply for this Role &nbsp;→
                  </button>
                </div>
              ))}
            </div>

            {/* Additional Job Information Bar */}
            <div className="hiring-info-bar">
              {hiringInfoItems.map((item, idx) => (
                <div key={idx} className="hiring-info-item">
                  <div className="hiring-info-icon" aria-hidden="true">{item.icon}</div>
                  <div className="hiring-info-text">
                    <strong>{item.label}</strong>
                    <p>{item.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Resume Submission Form Section */}
        <section className="resume-section-wrapper" id="resume-form-section">
          <div className="resume-inner-container">
            <div className="resume-card">
              <div className="resume-card-header">
                <span className="resume-badge">QUICK APPLICATION</span>
                <h2 className="resume-title">Submit Your Application</h2>
                <p className="resume-subtitle">
                  Ready to shape your future with ITSoftLab? Fill out the details below.
                  Our talent acquisition team will review your application and respond promptly.
                </p>
              </div>

              {successMessage && (
                <div className="resume-alert resume-alert--success" role="alert">
                  <span className="resume-alert-icon">✓</span>
                  <div>
                    <strong>Application Received!</strong>
                    <p>{successMessage}</p>
                  </div>
                </div>
              )}

              {errorMessage && (
                <div className="resume-alert resume-alert--error" role="alert">
                  <span className="resume-alert-icon">✕</span>
                  <div>
                    <strong>Submission Notice</strong>
                    <p>{errorMessage}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="resume-form" noValidate={false}>
                <div className="form-row form-row-2col">
                  <div className="form-group">
                    <label htmlFor="careers-firstName">First Name <span className="req">*</span></label>
                    <input
                      id="careers-firstName"
                      name="firstName"
                      type="text"
                      placeholder="e.g. Rahul"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="careers-lastName">Last Name <span className="req">*</span></label>
                    <input
                      id="careers-lastName"
                      name="lastName"
                      type="text"
                      placeholder="e.g. Sharma"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row form-row-2col">
                  <div className="form-group">
                    <label htmlFor="careers-email">Email Address <span className="req">*</span></label>
                    <input
                      id="careers-email"
                      name="email"
                      type="email"
                      placeholder="rahul.sharma@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="careers-phone">Phone Number <span className="req">*</span></label>
                    <input
                      id="careers-phone"
                      name="phone"
                      type="tel"
                      placeholder="+91 98765 43210"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row form-row-2col">
                  <div className="form-group">
                    <label htmlFor="careers-jobTitle">Role / Position <span className="req">*</span></label>
                    <input
                      id="careers-jobTitle"
                      name="jobTitle"
                      type="text"
                      placeholder="e.g. Senior MERN Stack Developer"
                      value={formData.jobTitle}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="careers-experience">Years of Experience <span className="req">*</span></label>
                    <input
                      id="careers-experience"
                      name="experience"
                      type="text"
                      placeholder="e.g. 2.5 Years"
                      value={formData.experience}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="careers-skills">Key Skills &amp; Technologies <span className="req">*</span></label>
                  <textarea
                    id="careers-skills"
                    name="skills"
                    placeholder="e.g. React.js, Node.js, Express, MongoDB, REST APIs, Git"
                    value={formData.skills}
                    onChange={handleChange}
                    rows="2"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="careers-education">Education &amp; Qualifications <span className="req">*</span></label>
                  <textarea
                    id="careers-education"
                    name="education"
                    placeholder="e.g. B.Tech in Computer Science / BCA / MCA"
                    value={formData.education}
                    onChange={handleChange}
                    rows="2"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="careers-additional">Additional Information / Cover Note <span className="opt">(Optional)</span></label>
                  <textarea
                    id="careers-additional"
                    name="additional"
                    placeholder="Portfolio URL, GitHub profile, notice period, or any relevant details..."
                    value={formData.additional}
                    onChange={handleChange}
                    rows="3"
                  />
                </div>

                {/* Custom File Upload Box */}
                <div className="form-group">
                  <label className="upload-label">
                    Upload Resume (PDF format) <span className="req">*</span>
                  </label>
                  <div
                    className={`file-upload-box ${file ? "has-file" : ""}`}
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  >
                    <input
                      ref={fileInputRef}
                      id="careers-file"
                      type="file"
                      accept="application/pdf"
                      onChange={handleFileChange}
                      className="hidden-file-input"
                    />
                    {file ? (
                      <div className="file-info-chip">
                        <span className="file-icon">📄</span>
                        <div className="file-details">
                          <span className="file-name">{file.name}</span>
                          <span className="file-size">
                            {(file.size / 1024 / 1024).toFixed(2)} MB • PDF Document
                          </span>
                        </div>
                        <button
                          type="button"
                          className="file-remove-btn"
                          onClick={handleRemoveFile}
                          title="Remove attached file"
                          aria-label="Remove attached file"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className="upload-prompt">
                        <div className="upload-icon-circle">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                          </svg>
                        </div>
                        <p className="upload-main-text">
                          <strong>Click or tap to upload your resume</strong>
                        </p>
                        <p className="upload-sub-text">Supported format: PDF (Max size: 10MB)</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="resume-submit-btn"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="submit-spinner-wrap">
                        <span className="submit-spinner"></span>
                        Submitting Application...
                      </span>
                    ) : (
                      "Submit Application &nbsp;→"
                    )}
                  </button>
                </div>
              </form>

              {/* Full-screen Loading Overlay */}
              {loading && (
                <div className="loader-overlay" aria-live="assertive" role="alert">
                  <div className="loader-popup-card">
                    <div className="loader-spinner"></div>
                    <p>Submitting your application...</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="cctta-section">
          <div className="cctta-container">
            <h2 className="cctta-title">Don't See the Right Position?</h2>
            <p className="cctta-text">
              We are constantly growing and eager to welcome exceptional talent.
              Send us your portfolio or reach out directly to discuss upcoming opportunities.
            </p>
            <div className="cctta-btn-wrap">
              <Link to="/contactus" className="cctta-button">
                Contact Our Recruitment Team &nbsp;→
              </Link>
            </div>
          </div>
        </section>

        {/* Standard ITSoftLab Responsive Footer */}
        <footer className="footer">
          <div className="footer-container">
            {/* Brand Column */}
            <div className="footer-section footer-brand">
              <div className="footer-logo-wrap">
                <img src="/images/log.png" alt="ITSoftLab Logo" className="footer-logo-img" />
              </div>
              <p>
                Empowering businesses with end-to-end 360° IT solutions,
                innovative digital products, and reliable technology services.
                We help organizations streamline operations, accelerate growth,
                and achieve digital transformation through cutting-edge software,
                web, mobile, cloud, and AI-driven solutions.
              </p>
              <p>
                Protect your data with top-notch cybersecurity measures. Awarded
                Best IT Company for consulting services and web development.
              </p>
            </div>

            {/* Quick Links Column */}
            <div className="footer-section">
              <h4>Quick Links</h4>
              <ul>
                <li><Link to="/">Home</Link></li>
                <li><Link to="/aboutPage">About Us</Link></li>
                <li><Link to="/servicespage">Services</Link></li>
                <li><Link to="/careerpage">Careers</Link></li>
                <li><Link to="/contactus">Contact Us</Link></li>
                <li><Link to="/privacypage">Privacy Policy</Link></li>
                <li><Link to="/termpage">Terms &amp; Conditions</Link></li>
                <li><Link to="/products">Products</Link></li>
              </ul>
            </div>

            {/* Our Services Column */}
            <div className="footer-section">
              <h4>Our Services</h4>
              <ul>
                <li><Link to="/servicespage">Android / iOS App Development</Link></li>
                <li><Link to="/servicespage">Web App Development</Link></li>
                <li><Link to="/servicespage">Software Development</Link></li>
                <li><Link to="/servicespage">Cloud &amp; SaaS / PaaS / IaaS</Link></li>
                <li><Link to="/servicespage">Cyber Security</Link></li>
                <li><Link to="/servicespage">Disaster Recovery</Link></li>
                <li><Link to="/servicespage">AI / ML Solutions</Link></li>
                <li><Link to="/servicespage">DevOps &amp; Automation</Link></li>
              </ul>
            </div>

            {/* Our Products Column */}
            <div className="footer-section">
              <h4>Our Products</h4>
              <ul>
                <li><Link to="/crm">CRM</Link></li>
                <li><a href="https://hrmsm.com/home" target="_blank" rel="noopener noreferrer">HRMS</a></li>
                <li><Link to="/contactus">VMS</Link></li>
                <li><Link to="/contactus">ERP</Link></li>
                <li><Link to="/contactus">DMS</Link></li>
                <li><Link to="/contactus">LMS</Link></li>
                <li><Link to="/contactus">POS</Link></li>
                <li><Link to="/contactus">Marketing Automation</Link></li>
              </ul>
            </div>
          </div>

          <hr className="footer-hr" />
          <p className="footer-bottom">© 2026 IT SOFTLAB. All rights reserved.</p>
        </footer>
      </main>
    </>
  );
}
